// The phone channel header's ⋯ menu (#348) and the channels Settings → Stats → Channel stats lists. One rule for
// every client; the cases are shared/cases/header-menu.json, which the Swift and Kotlin ports run too.
import type { Category, Channel, NotifyLevel } from "./protocol.ts";

export type HeaderMenuId = "members" | "pins" | "notifications";

export interface HeaderMenuInput {
  /** False where a client leaves Members out of the menu (a DM on Android). */
  members: boolean;
  /** People online in the server. */
  online: number;
  /** Pinned messages in the channel. */
  pins: number;
  /** The level that applies to this conversation for me (`effectiveNotifyLevel`). */
  level: NotifyLevel;
  /** Muted now (the conversation, or the server for a channel). */
  muted: boolean;
}

export interface HeaderMenuRow {
  id: HeaderMenuId;
  label: string;
  /** The small text at the end of the row; "" for none. */
  hint: string;
}

/** The pin badge the old header icon had: the count, "99+" over 99, nothing for none. */
export const pinCountText = (n: number): string => {
  const c = Number.isFinite(n) ? Math.max(0, Math.floor(n)) : 0;
  return c === 0 ? "" : c > 99 ? "99+" : String(c);
};

/** What the Notifications row says about the conversation. A mute wins over the level, as the bell's icon does. */
export const notifyHint = (level: NotifyLevel, muted: boolean): string => (muted ? "Muted" : level === "all" ? "All messages" : level === "mentions" ? "Mentions" : "Nothing");

/** The rows of the ⋯ menu, in order: Members, Pins, Notifications. */
export function headerMenuRows(i: HeaderMenuInput): HeaderMenuRow[] {
  const online = Number.isFinite(i.online) ? Math.max(0, Math.floor(i.online)) : 0;
  const rows: HeaderMenuRow[] = [];
  if (i.members) rows.push({ id: "members", label: "Members", hint: `${online} online` });
  rows.push({ id: "pins", label: "Pins", hint: pinCountText(i.pins) });
  rows.push({ id: "notifications", label: "Notifications", hint: notifyHint(i.level, i.muted) });
  return rows;
}

/**
 * The channels Settings → Stats → Channel stats can pick: every text channel (a DM has no channel stats), in the
 * sidebar's order: channels in no category first, then each category by position, each channel by position and then
 * name. Names compare by code unit, so every client orders a tie the same way.
 */
export function statsChannelChoices(channels: Pick<Channel, "id" | "name" | "kind" | "categoryId" | "position">[], categories: Pick<Category, "id" | "position">[]): string[] {
  const rank = new Map(categories.map((c) => [c.id, c.position]));
  const order = (c: { categoryId: string | null }) => (c.categoryId === null || !rank.has(c.categoryId) ? -Infinity : rank.get(c.categoryId)!);
  return channels
    .filter((c) => c.kind === "text")
    .sort((a, b) => {
      const ra = order(a);
      const rb = order(b);
      if (ra !== rb) return ra < rb ? -1 : 1;
      if (a.position !== b.position) return a.position - b.position;
      return a.name < b.name ? -1 : a.name > b.name ? 1 : a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
    })
    .map((c) => c.id);
}

/**
 * The channel Channel stats opens on: the one you came from when it is a text channel in the list, else the first
 * choice, else none (a server with no text channel).
 */
export function statsChannelStart(current: string | null | undefined, choices: string[]): string | null {
  return current && choices.includes(current) ? current : (choices[0] ?? null);
}
